import type { TicketDevice } from '@/lib/api/tickets.js';
import { deviceLabel } from '@/components/ticket-detail/DeviceCardDetail.js';
import { cn } from '@/lib/utils.js';

function DeviceStatusMark({ status }: { status?: TicketDevice['workStatus'] }) {
  if (status === 'repaired') {
    return <span className="text-emerald-600" aria-label="Repaired">✓</span>;
  }
  const tone =
    status === 'in_repair'
      ? 'text-blue-600'
      : status === 'waiting_parts'
        ? 'text-amber-500'
        : 'text-slate-400';
  const label =
    status === 'in_repair' ? 'In repair' : status === 'waiting_parts' ? 'Waiting parts' : 'Pending';
  return (
    <span className={tone} aria-label={label}>
      ●
    </span>
  );
}

export function DeviceTabs({
  devices,
  selectedId,
  onSelect,
}: {
  devices: TicketDevice[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  if (devices.length < 2) return null;

  return (
    <div
      className="flex gap-2 overflow-x-auto pb-1"
      role="tablist"
      aria-label="Devices on this ticket"
    >
      {devices.map((device) => {
        const selected = device.id === selectedId;
        return (
          <button
            key={device.id}
            type="button"
            role="tab"
            aria-selected={selected}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold',
              selected
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
            )}
            onClick={() => onSelect(device.id)}
          >
            <span className="max-w-[14rem] truncate">{deviceLabel(device)}</span>
            <DeviceStatusMark status={device.workStatus} />
          </button>
        );
      })}
    </div>
  );
}
